import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { employerAPI, uploadAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { FaBuilding, FaUser, FaFileUpload, FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import { validateForm as validateFormUtil } from '../../utils/validation';

const EmployerKYC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [employer, setEmployer] = useState(null);
    const [currentStep, setCurrentStep] = useState(parseInt(searchParams.get('step')) || 1);
    const [errors, setErrors] = useState({});

    // Refs for safe state management
    const isMounted = useRef(true);
    const hasLoadedRef = useRef(false);

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    const [formData, setFormData] = useState({
        // Company Information
        company_name: '',
        company_description: '',
        company_website: '',
        company_registration_number: '',
        company_address: '',
        company_city: '',
        company_state: '',
        company_country: '',
        company_postal_code: '',
        company_phone: '',
        company_email: '',

        // Representative Information
        representative_name: '',
        representative_designation: '',
        representative_email: '',
        representative_phone: '',
        representative_id_type: '',
        representative_id_number: '',

        // Business Information
        tax_id_number: '',
        business_license_number: '',

        // Banking Information
        bank_name: '',
        bank_account_number: '',
        bank_routing_number: ''
    });

    const [documents, setDocuments] = useState({
        company_registration_doc: null,
        representative_photo: null,
        representative_id_doc: null,
        tax_certificate: null,
        business_license: null,
        bank_statement: null
    });

    const [uploading, setUploading] = useState({});

    const loadEmployerProfile = useCallback(async () => {
        if (hasLoadedRef.current) return;

        try {
            setLoading(true);
            const response = await employerAPI.getProfile();

            if (!isMounted.current) return;

            if (response.success && response.employer) {
                const emp = response.employer;
                setEmployer(prev => JSON.stringify(prev) === JSON.stringify(emp) ? prev : emp);

                // Pre-fill form if KYC already submitted
                if (['submitted', 'under_review'].includes(emp.kyc_status)) {
                    setFormData(prev => {
                        const next = {
                            ...prev,
                            company_name: emp.company_name || '',
                            company_description: emp.company_description || '',
                            company_website: emp.company_website || '',
                            company_registration_number: emp.company_registration_number || '',
                            company_address: emp.company_address || '',
                            company_city: emp.company_city || '',
                            company_state: emp.company_state || '',
                            company_country: emp.company_country || '',
                            company_postal_code: emp.company_postal_code || '',
                            company_phone: emp.company_phone || '',
                            company_email: emp.company_email || '',
                            representative_name: emp.representative_name || '',
                            representative_designation: emp.representative_designation || '',
                            representative_email: emp.representative_email || '',
                            representative_phone: emp.representative_phone || '',
                            representative_id_type: emp.representative_id_type || '',
                            representative_id_number: emp.representative_id_number || '',
                            tax_id_number: emp.tax_id_number || '',
                            business_license_number: emp.business_license_number || '',
                            bank_name: emp.bank_name || '',
                            bank_account_number: emp.bank_account_number || '',
                            bank_routing_number: emp.bank_routing_number || ''
                        };
                        return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
                    });
                }

                // Only redirect if COMPLIANT as well, otherwise we loop with dashboard
                if (emp.kyc_status === 'approved' && emp.compliance_status === 'compliant') {
                    navigate('/dashboard');
                }

                hasLoadedRef.current = true;
            }
        } catch (error) {
            console.error('Failed to load employer profile:', error);
            if (isMounted.current) toast.error('Failed to load profile. Please try again.');
        } finally {
            if (isMounted.current) setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        loadEmployerProfile();
    }, [loadEmployerProfile]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleFileUpload = async (field, file) => {
        if (!file) return;

        try {
            setUploading(prev => ({ ...prev, [field]: true }));
            const response = await uploadAPI.uploadFile(file);
            setDocuments(prev => ({
                ...prev,
                [field]: response.file.filename
            }));
            toast.success('Document uploaded successfully');
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Failed to upload document. Please try again.');
        } finally {
            setUploading(prev => ({ ...prev, [field]: false }));
        }
    };

    const getStepRules = (step) => {
        if (step === 1) {
            return {
                company_name: { required: true, minLength: 3, label: 'Company Name' },
                company_description: { required: true, minLength: 10, label: 'Company Description' },
                company_registration_number: { required: true, label: 'Registration Number' },
                company_address: { required: true, label: 'Company Address' },
                company_city: { required: true, label: 'City' },
                company_country: { required: true, label: 'Country' },
                company_phone: { required: true, type: 'phone', label: 'Company Phone' },
                company_email: { required: true, type: 'email', label: 'Company Email' },
                company_website: { type: 'url', label: 'Company Website' }
            };
        } else if (step === 2) {
            return {
                representative_name: { required: true, minLength: 3, label: 'Representative Name' },
                representative_designation: { required: true, label: 'Designation' },
                representative_email: { required: true, type: 'email', label: 'Representative Email' },
                representative_phone: { required: true, type: 'phone', label: 'Representative Phone' },
                representative_id_type: { required: true, label: 'ID Type' },
                representative_id_number: { required: true, label: 'ID Number' },
                tax_id_number: { required: true, label: 'Tax ID Number' },
                business_license_number: { required: true, label: 'Business License Number' }
            };
        }
        return null;
    };

    const isStepValid = (step) => {
        const rules = getStepRules(step);
        if (!rules) return true;
        const newErrors = validateFormUtil(formData, rules);
        return Object.keys(newErrors).length === 0;
    };

    const validateAndSetErrors = (step) => {
        const rules = getStepRules(step);
        if (!rules) return true;
        const newErrors = validateFormUtil(formData, rules);
        setErrors(prev => ({ ...prev, ...newErrors }));
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateAndSetErrors(1) || !validateAndSetErrors(2)) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            setSubmitting(true);

            // Upload documents first
            const documentPaths = {};
            for (const [key, file] of Object.entries(documents)) {
                if (file && typeof file === 'object') {
                    const response = await uploadAPI.uploadFile(file);
                    documentPaths[key.replace('_doc', '_doc_path').replace('_photo', '_photo_path').replace('_statement', '_statement_path')] = response.file.filename;
                }
            }

            // Upload documents if any
            if (Object.keys(documentPaths).length > 0) {
                await employerAPI.uploadKYCDocuments(documentPaths);
            }

            // Submit KYC information
            await employerAPI.submitKYC(formData);

            toast.success('KYC information submitted successfully! Awaiting review by Student Manager or Admin.');
            navigate('/dashboard');
        } catch (error) {
            console.error('KYC submission error:', error);
            toast.error(error?.response?.data?.error || 'Failed to submit KYC. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
            {loading ? (
                <div className="text-center">
                    <FaSpinner className="animate-spin text-4xl text-blue-600 mx-auto mb-4" />
                    <p className="text-gray-600">Loading...</p>
                </div>
            ) : employer?.kyc_status === 'approved' && employer?.compliance_status === 'compliant' ? (
                <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
                    <FaCheckCircle className="text-6xl text-green-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">KYC Approved</h2>
                    <p className="text-gray-600 mb-6">Your KYC verification and compliance have been approved. You can now post job opportunities.</p>
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="btn-primary w-full"
                    >
                        Go to Dashboard
                    </button>
                </div>
            ) : employer?.kyc_status === 'rejected' ? (
                <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
                    <FaTimesCircle className="text-6xl text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">KYC Rejected</h2>
                    {employer.kyc_rejection_reason && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4 text-left">
                            <p className="text-sm font-medium text-red-800 mb-1">Reason:</p>
                            <p className="text-sm text-red-700">{employer.kyc_rejection_reason}</p>
                        </div>
                    )}
                    <p className="text-gray-600 mb-6">Please review the feedback and resubmit your KYC information.</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="btn-primary w-full"
                    >
                        Update KYC Information
                    </button>
                </div>
            ) : (
                <div className="max-w-4xl mx-auto w-full">
                    <div className="bg-white rounded-lg shadow-lg p-8">
                        <div className="mb-8">
                            <h1 className="text-3xl font-bold text-gray-800 mb-2">Employer KYC Verification</h1>
                            <p className="text-gray-600">
                                Please provide the following information to verify your business. This information will be reviewed by Student Manager or Admin.
                            </p>
                        </div>

                        {/* Progress Steps */}
                        <div className="mb-8 flex items-center justify-between">
                            {[1, 2, 3].map((step) => (
                                <div key={step} className="flex items-center flex-1">
                                    <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${currentStep >= step
                                        ? 'bg-blue-600 border-blue-600 text-white'
                                        : 'border-gray-300 text-gray-400'
                                        }`}>
                                        {currentStep > step ? <FaCheckCircle /> : step}
                                    </div>
                                    {step < 3 && (
                                        <div className={`flex-1 h-1 mx-2 ${currentStep > step ? 'bg-blue-600' : 'bg-gray-300'
                                            }`} />
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-between text-sm text-gray-600 mb-8">
                            <span>Company Info</span>
                            <span>Representative</span>
                            <span>Documents</span>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-8">
                            {/* Step 1: Company Information */}
                            {currentStep === 1 && (
                                <div className="space-y-6">
                                    <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
                                        <FaBuilding /> Company Information
                                    </h2>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Company Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="company_name"
                                                value={formData.company_name}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 ${errors.company_name ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                            />
                                            {errors.company_name && <p className="text-red-500 text-xs mt-1">{errors.company_name}</p>}
                                        </div>

                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Company Description <span className="text-red-500">*</span>
                                            </label>
                                            <textarea
                                                name="company_description"
                                                value={formData.company_description}
                                                onChange={handleChange}
                                                rows="3"
                                                className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 ${errors.company_description ? 'border-red-500' : 'border-gray-300'}`}
                                            />
                                            {errors.company_description && <p className="text-red-500 text-xs mt-1">{errors.company_description}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Company Website
                                            </label>
                                            <input
                                                type="url"
                                                name="company_website"
                                                value={formData.company_website}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 ${errors.company_website ? 'border-red-500' : 'border-gray-300'}`}
                                            />
                                            {errors.company_website && <p className="text-red-500 text-xs mt-1">{errors.company_website}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Registration Number <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="company_registration_number"
                                                value={formData.company_registration_number}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 ${errors.company_registration_number ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                            />
                                            {errors.company_registration_number && <p className="text-red-500 text-xs mt-1">{errors.company_registration_number}</p>}
                                        </div>

                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Company Address <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="company_address"
                                                value={formData.company_address}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 ${errors.company_address ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                            />
                                            {errors.company_address && <p className="text-red-500 text-xs mt-1">{errors.company_address}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                City <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="company_city"
                                                value={formData.company_city}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 ${errors.company_city ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                            />
                                            {errors.company_city && <p className="text-red-500 text-xs mt-1">{errors.company_city}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                State/Province
                                            </label>
                                            <input
                                                type="text"
                                                name="company_state"
                                                value={formData.company_state}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Country <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="company_country"
                                                value={formData.company_country}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 ${errors.company_country ? 'border-red-500' : 'border-gray-300'}`}
                                            />
                                            {errors.company_country && <p className="text-red-500 text-xs mt-1">{errors.company_country}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Postal Code
                                            </label>
                                            <input
                                                type="text"
                                                name="company_postal_code"
                                                value={formData.company_postal_code}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Company Phone <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="tel"
                                                name="company_phone"
                                                value={formData.company_phone}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 ${errors.company_phone ? 'border-red-500' : 'border-gray-300'}`}
                                            />
                                            {errors.company_phone && <p className="text-red-500 text-xs mt-1">{errors.company_phone}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Company Email <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                name="company_email"
                                                value={formData.company_email}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 ${errors.company_email ? 'border-red-500' : 'border-gray-300'}`}
                                            />
                                            {errors.company_email && <p className="text-red-500 text-xs mt-1">{errors.company_email}</p>}
                                        </div>
                                    </div>

                                    <div className="flex justify-end gap-4">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (validateAndSetErrors(1)) {
                                                    setCurrentStep(2);
                                                }
                                            }}
                                            className="btn-primary"
                                            disabled={!isStepValid(1)}
                                        >
                                            Next: Representative Information
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Step 2: Representative Information */}
                            {currentStep === 2 && (
                                <div className="space-y-6">
                                    <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
                                        <FaUser /> Representative Information
                                    </h2>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Representative Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="representative_name"
                                                value={formData.representative_name}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 ${errors.representative_name ? 'border-red-500' : 'border-gray-300'}`}
                                            />
                                            {errors.representative_name && <p className="text-red-500 text-xs mt-1">{errors.representative_name}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Designation <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="representative_designation"
                                                value={formData.representative_designation}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 ${errors.representative_designation ? 'border-red-500' : 'border-gray-300'}`}
                                            />
                                            {errors.representative_designation && <p className="text-red-500 text-xs mt-1">{errors.representative_designation}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Representative Email <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                name="representative_email"
                                                value={formData.representative_email}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 ${errors.representative_email ? 'border-red-500' : 'border-gray-300'}`}
                                            />
                                            {errors.representative_email && <p className="text-red-500 text-xs mt-1">{errors.representative_email}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Representative Phone <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="tel"
                                                name="representative_phone"
                                                value={formData.representative_phone}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 ${errors.representative_phone ? 'border-red-500' : 'border-gray-300'}`}
                                            />
                                            {errors.representative_phone && <p className="text-red-500 text-xs mt-1">{errors.representative_phone}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                ID Type <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                name="representative_id_type"
                                                value={formData.representative_id_type}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 ${errors.representative_id_type ? 'border-red-500' : 'border-gray-300'}`}
                                            >
                                                <option value="">Select ID Type</option>
                                                <option value="passport">Passport</option>
                                                <option value="national_id">National ID</option>
                                                <option value="drivers_license">Driver's License</option>
                                                <option value="other">Other</option>
                                            </select>
                                            {errors.representative_id_type && <p className="text-red-500 text-xs mt-1">{errors.representative_id_type}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                ID Number <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="representative_id_number"
                                                value={formData.representative_id_number}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 ${errors.representative_id_number ? 'border-red-500' : 'border-gray-300'}`}
                                            />
                                            {errors.representative_id_number && <p className="text-red-500 text-xs mt-1">{errors.representative_id_number}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Tax ID Number <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="tax_id_number"
                                                value={formData.tax_id_number}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 ${errors.tax_id_number ? 'border-red-500' : 'border-gray-300'}`}
                                            />
                                            {errors.tax_id_number && <p className="text-red-500 text-xs mt-1">{errors.tax_id_number}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Business License Number <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="business_license_number"
                                                value={formData.business_license_number}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 ${errors.business_license_number ? 'border-red-500' : 'border-gray-300'}`}
                                            />
                                            {errors.business_license_number && <p className="text-red-500 text-xs mt-1">{errors.business_license_number}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Bank Name
                                            </label>
                                            <input
                                                type="text"
                                                name="bank_name"
                                                value={formData.bank_name}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Bank Account Number
                                            </label>
                                            <input
                                                type="text"
                                                name="bank_account_number"
                                                value={formData.bank_account_number}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Bank Routing Number
                                            </label>
                                            <input
                                                type="text"
                                                name="bank_routing_number"
                                                value={formData.bank_routing_number}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex justify-between gap-4">
                                        <button
                                            type="button"
                                            onClick={() => setCurrentStep(1)}
                                            className="px-6 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (validateAndSetErrors(2)) {
                                                    setCurrentStep(3);
                                                }
                                            }}
                                            className="btn-primary"
                                            disabled={!isStepValid(2)}
                                        >
                                            Next: Documents
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Documents */}
                            {currentStep === 3 && (
                                <div className="space-y-6">
                                    <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
                                        <FaFileUpload /> Upload Documents
                                    </h2>
                                    <p className="text-gray-600">
                                        Upload the following documents to complete your KYC verification. All documents are required.
                                    </p>

                                    <div className="space-y-4">
                                        {[
                                            { key: 'company_registration_doc', label: 'Company Registration Document', required: true },
                                            { key: 'representative_photo', label: 'Representative Photo', required: true },
                                            { key: 'representative_id_doc', label: 'Representative ID Document', required: true },
                                            { key: 'tax_certificate', label: 'Tax Certificate', required: false },
                                            { key: 'business_license', label: 'Business License', required: false },
                                            { key: 'bank_statement', label: 'Bank Statement', required: false }
                                        ].map((doc) => (
                                            <div key={doc.key} className="border border-gray-300 rounded-md p-4">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {doc.label} {doc.required && <span className="text-red-500">*</span>}
                                                </label>
                                                <div className="flex items-center gap-4">
                                                    <input
                                                        type="file"
                                                        accept=".pdf,.jpg,.jpeg,.png"
                                                        onChange={(e) => {
                                                            const file = e.target.files[0];
                                                            if (file) {
                                                                setDocuments(prev => ({ ...prev, [doc.key]: file }));
                                                                handleFileUpload(doc.key, file);
                                                            }
                                                        }}
                                                        className="flex-1 text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                                    />
                                                    {uploading[doc.key] && (
                                                        <FaSpinner className="animate-spin text-blue-600" />
                                                    )}
                                                    {documents[doc.key] && !uploading[doc.key] && (
                                                        <FaCheckCircle className="text-green-500" />
                                                    )}
                                                </div>
                                                {documents[doc.key] && typeof documents[doc.key] === 'string' && (
                                                    <p className="text-xs text-gray-500 mt-1">Uploaded: {documents[doc.key]}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    <div className="flex justify-between gap-4">
                                        <button
                                            type="button"
                                            onClick={() => setCurrentStep(2)}
                                            className="px-6 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn-primary"
                                            disabled={submitting || !documents.company_registration_doc || !documents.representative_photo || !documents.representative_id_doc}
                                        >
                                            {submitting ? 'Submitting...' : 'Submit KYC'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployerKYC;
